// packages/utils/src/extractApiError.ts

/**
 * A single field-level validation failure as emitted by the backend
 * AllExceptionsFilter for VALIDATION_FAILED (422).
 */
export type ApiFieldError = {
  property: string;
  constraints?: Record<string, string>;
};

/**
 * Shape of an API error after narrowing.
 * All fields optional because the underlying shape can't be guaranteed.
 *
 * The backend AllExceptionsFilter returns `{ code, message, errors?, details?, timestamp }`
 * with `code` at the ROOT (not nested under `error`), `errors` as the field-level list
 * for class-validator failures, and `details` reserved for constraint metadata.
 */
export type ApiError = {
  status?: number;
  code?: string;
  message?: string;
  errors?: ApiFieldError[];
  details?: unknown;
  debug?: {
    name?: string;
    code?: string;
    message?: string;
    stack?: string;
    errors?: unknown;
    details?: unknown;
    cause?: unknown;
    response?: unknown;
    [key: string]: unknown;
  };
};

/**
 * Narrow an unknown error (likely an AxiosError) into our API error shape.
 * Never throws — returns an empty object if the shape doesn't match.
 * Used by onError callbacks across the app to avoid `any`.
 */
export function extractApiError(err: unknown): ApiError {
  if (typeof err !== 'object' || err === null) return {};
  const maybe = err as { response?: { status?: number; data?: ApiError } };
  return {
    status: maybe.response?.status,
    ...(maybe.response?.data ?? {}),
  };
}

/** `response.data` when it is an object; `undefined` for any other shape. */
function readErrorBody(err: unknown): Record<string, unknown> | undefined {
  if (typeof err !== 'object' || err === null) return undefined;
  const data = (err as { response?: { data?: unknown } }).response?.data;
  if (typeof data !== 'object' || data === null) return undefined;
  return data as Record<string, unknown>;
}

function asNonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/**
 * The backend error code, read from the ROOT of the response body
 * (`{ code, message, details?, errors?, timestamp }`).
 *
 * It is the i18n key of the error catalog (`errors:<CODE>`) and the value every
 * control-flow branch compares against. Returns `undefined` for a body without
 * `code`, for a transport error with no response, and for anything that is not
 * an axios error — never throws.
 */
export function getApiErrorCode(err: unknown): string | undefined {
  return asNonEmptyString(readErrorBody(err)?.code);
}

/**
 * The human-readable message the backend sent, for when the code has no entry
 * in the error catalog. Prefer showing the translated code when there is one.
 */
export function getApiErrorMessage(err: unknown): string | undefined {
  return asNonEmptyString(readErrorBody(err)?.message);
}

/**
 * The `details` payload of the error body — constraint metadata that varies per
 * code (`blockers` for ENTITY_HAS_CHILDREN, field → messages for 422, ...).
 * The caller declares the shape it expects; only objects are returned.
 *
 * Field-level validation failures travel in `errors[]`, not here — read those
 * with `extractApiError(err).errors` + `fieldErrorsFromApi`.
 */
export function getApiErrorDetails<TDetails = unknown>(err: unknown): TDetails | undefined {
  const details = readErrorBody(err)?.details;
  if (typeof details !== 'object' || details === null) return undefined;
  return details as TDetails;
}

/**
 * Transport failure: the request left but no response came back. An error
 * carrying a `response` is a backend answer, never a network error.
 */
export function isNetworkError(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false;
  const candidate = err as { code?: unknown; response?: unknown };
  if (candidate.response !== undefined) return false;
  return candidate.code === 'ERR_NETWORK' || candidate.code === 'ECONNABORTED';
}

/**
 * Flatten the backend `errors[]` validation list into `[property, firstConstraintMessage]`
 * pairs, ready to feed react-hook-form's `setError`. Skips entries without a usable message.
 */
export function fieldErrorsFromApi(errors: ApiFieldError[] | undefined): Array<[string, string]> {
  if (!Array.isArray(errors)) return [];
  const pairs: Array<[string, string]> = [];
  for (const entry of errors) {
    if (!entry || typeof entry.property !== 'string' || entry.property.length === 0) continue;
    const messages = entry.constraints ? Object.values(entry.constraints) : [];
    const message = messages.find((m) => typeof m === 'string' && m.length > 0);
    if (message) pairs.push([entry.property, message]);
  }
  return pairs;
}
